// Service Catalog API Client for  Āvāhana Platform

import axios from "axios";
import { createLogger } from "../lib/logger";
import { CreateServiceResponse } from "../types";
import {
  CreateServiceFromCatalogData,
  ServiceCatalogItem,
  ServiceCatalogResponse,
  ServiceCatalogSearchResponse,
  ServiceCategoriesResponse,
  ServiceCategory,
  ServiceTemplateResponse,
} from "../types/service-catalog";

// API Base URLs
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8003/api";
const SERVICE_CATALOG_BASE = `${API_BASE}/service-catalog`;

const log = createLogger("ServiceCatalogAPI");

/**
 * Service Catalog API Client
 * Provides methods to interact with the service catalog system
 */
export class ServiceCatalogAPI {
  private static authHeader(): Record<string, string> {
    const token = localStorage.getItem("avahana-token") || localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Get all service templates organized by category
   */
  static async getAllTemplates(): Promise<
    Record<string, ServiceCatalogItem[]>
  > {
    try {
      const response = await axios.get<ServiceCatalogResponse>(
        `${SERVICE_CATALOG_BASE}/templates`,
        { headers: ServiceCatalogAPI.authHeader() }
      );
      return response.data.data;
    } catch (error) {
      log.error("Failed to fetch service templates", error);
      throw new Error("Failed to load service templates");
    }
  }

  /**
   * Get all service categories
   */
  static async getCategories(): Promise<ServiceCategory[]> {
    try {
      const response = await axios.get<ServiceCategoriesResponse>(
        `${SERVICE_CATALOG_BASE}/categories`
      );
      return response.data.data;
    } catch (error) {
      log.error("Failed to fetch service categories", error);
      throw new Error("Failed to load service categories");
    }
  }

  /**
   * Search service templates
   */
  static async searchTemplates(query: string): Promise<ServiceCatalogItem[]> {
    try {
      const response = await axios.get<ServiceCatalogSearchResponse>(
        `${SERVICE_CATALOG_BASE}/search?q=${encodeURIComponent(query)}`,
        { headers: ServiceCatalogAPI.authHeader() }
      );
      return response.data.data;
    } catch (error) {
      log.error("Failed to search service templates", error);
      throw new Error("Failed to search service templates");
    }
  }

  /**
   * Get templates by category
   */
  static async getTemplatesByCategory(
    category: string
  ): Promise<ServiceCatalogItem[]> {
    try {
      const response = await axios.get<ServiceCatalogSearchResponse>(
        `${SERVICE_CATALOG_BASE}/templates/category/${encodeURIComponent(category)}`
      );
      return response.data.data;
    } catch (error) {
      log.error("Failed to fetch templates by category", error);
      throw new Error("Failed to load templates for category");
    }
  }

  /**
   * Get a specific service template by type
   */
  static async getTemplate(templateType: string): Promise<ServiceCatalogItem> {
    try {
      const response = await axios.get<ServiceTemplateResponse>(
        `${SERVICE_CATALOG_BASE}/templates/${encodeURIComponent(templateType)}`
      );
      return response.data.data;
    } catch (error) {
      log.error(`Failed to fetch template ${templateType}`, error);
      throw new Error(`Failed to load template: ${templateType}`);
    }
  }

  /**
   * Initialize the service catalog with default templates
   * (Admin function - should be called during setup)
   */
  static async initializeCatalog(): Promise<void> {
    try {
      await axios.post(`${SERVICE_CATALOG_BASE}/initialize`);
    } catch (error) {
      log.error("Failed to initialize service catalog", error);
      throw new Error("Failed to initialize service catalog");
    }
  }

  /**
   * Create a service from a catalog template
   */
  static async createServiceFromTemplate(
    projectId: string,
    data: CreateServiceFromCatalogData
  ): Promise<CreateServiceResponse> {
    try {
      // First get the template to extract configuration
      const template = await this.getTemplate(data.templateType);

      // Prefer the framework the caller already resolved from the selected template
      // (data.templateConfig.framework). Only fall back to re-fetching from the
      // template if the caller didn't provide one — the re-fetched template may be
      // the wrong entry when multiple services share the same `type` (e.g. both
      // "mlflow" and "openobserve" have type "observability").
      const framework =
        data.templateConfig?.framework ||
        template.defaultConfig.framework;

      const serviceData = {
        name: data.name,
        // Use the catalog's type field ("observability", "database-vector", etc.)
        // framework is stored separately for driver dispatch.
        type: data.templateType || framework || this.mapTemplateTypeToServiceType(data.templateType),
        repository: data.repository,
        framework,
        buildCommand:
          data.templateConfig?.buildCommand ||
          template.defaultConfig.buildCommand,
        startCommand:
          data.templateConfig?.startCommand ||
          template.defaultConfig.startCommand,
        environmentId: data.environmentId,
        redirectToDeployment: data.redirectToDeployment,
        customCredentials: data.customCredentials,
      };

      // Create the service using existing API
      const response = await axios.post<CreateServiceResponse>(
        `${API_BASE}/projects/${projectId}/services`,
        serviceData
      );

      return response.data;
    } catch (error) {
      log.error("Failed to create service from template", error);
      throw new Error("Failed to create service from template");
    }
  }

  /**
   * Map template type to existing ServiceType enum
   * This bridges the catalog system with the existing service types
   */
  private static mapTemplateTypeToServiceType(templateType: string): string {
    // Map common template types to existing service types
    const typeMapping: Record<string, string> = {
      "web-frontend": "web-frontend",
      "web-backend": "web-backend",
      "static-site": "static",
      worker: "worker",
      // Database types - both specific and generic
      "database-relational": "database-relational", // Generic SQL database
      "database-nosql": "database-nosql", // Generic NoSQL database
      "database-postgresql": "database-relational",
      "database-mysql": "database-relational",
      "database-mongodb": "database-nosql",
      "database-redis": "database-redis",
      postgresql: "database-relational", // Alias
      postgres: "database-relational", // Alias
      mysql: "database-relational", // Alias
      mongodb: "database-nosql", // Alias
      redis: "database-redis", // Alias
      // Vector databases
      "database-vector": "database-vector", // Generic vector database
      qdrant: "qdrant", // Qdrant vector DB
      chroma: "chroma", // Chroma vector DB
      weaviate: "weaviate", // Weaviate vector DB
      milvus: "milvus", // Milvus vector DB
      pgvector: "pgvector", // PostgreSQL with vector extension
      function: "function",
      "cron-job": "cron",
      "api-gateway": "api-gateway",
      // Add mappings for documented services
      "version-control-gitea": "web-backend",
      "version-control-forgejo": "web-backend",
      "version-control-gitkraken": "web-backend",
      "version-control-sourcehut": "web-backend",
      "object-storage-minio": "web-backend",
      "object-storage-garage": "web-backend",
      "file-management-nextcloud": "web-backend",
      "file-management-owncloud": "web-backend",
      "file-management-seafile": "web-backend",
    };

    return typeMapping[templateType] || "web-backend"; // Default fallback
  }

  /**
   * Get popular/featured templates
   */
  static async getPopularTemplates(): Promise<ServiceCatalogItem[]> {
    try {
      const allTemplates = await this.getAllTemplates();
      const popularTemplates: ServiceCatalogItem[] = [];

      // Flatten all categories and filter for popular templates
      Object.values(allTemplates).forEach((categoryTemplates) => {
        popularTemplates.push(
          ...categoryTemplates.filter((template) => template.isPopular)
        );
      });

      return popularTemplates;
    } catch (error) {
      log.error("Failed to fetch popular templates", error);
      throw new Error("Failed to load popular templates");
    }
  }

  /**
   * Get recently added templates
   */
  static async getRecentTemplates(
    limit: number = 5
  ): Promise<ServiceCatalogItem[]> {
    try {
      const allTemplates = await this.getAllTemplates();
      const recentTemplates: ServiceCatalogItem[] = [];

      // Flatten all categories
      Object.values(allTemplates).forEach((categoryTemplates) => {
        recentTemplates.push(...categoryTemplates);
      });

      // Sort by creation date and limit
      return recentTemplates
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, limit);
    } catch (error) {
      log.error("Failed to fetch recent templates", error);
      throw new Error("Failed to load recent templates");
    }
  }
}

export default ServiceCatalogAPI;
